import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, Outlet, useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTachometerAlt,
  faBox,
  faPlus,
  faUser,
  faBell,
  faBars,
  faTimes,
  faSignOutAlt,
  faWallet,
  faSearchLocation,
  faArrowLeft,
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
  faHome,
  faHistory,
  faCrown,
  faChevronRight,
  faTruck,
  faMapMarkerAlt,
  faBoxOpen
} from '@fortawesome/free-solid-svg-icons';

// Updated imports to match new structure
import MyShipments from './Shipments/MyShipments';
import NewShipment from './Shipments/NewShipment';
import Tracking from './Shipments/Tracking';
import Profile from './Profile';

// Add services imports
import { userService } from '../../../services/userService';
import { shipmentService } from '../../../services/shipmentService';
import { pushService } from '../../../services/pushService';
import AnnouncementBanner from '../../../components/AnnouncementBanner';
import useWelcomeOnce from '../../../hooks/useWelcomeOnce';

export const UserDashboardHome = () => {
  const [stats, setStats] = useState({ totalShipments: 0, inTransit: 0, delivered: 0, pending: 0 });
  const [recentShipments, setRecentShipments] = useState([]);
  const navigate = useNavigate();
  const userLocal = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  })();

  useEffect(() => {
    if (!userLocal) return;
    const needsProfile = !userLocal.phone || !userLocal.address;
    if (needsProfile) {
      navigate('/dashboard/user/profile');
    }
  }, [userLocal]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    (async () => {
      try {
        const data = await shipmentService.getMy(token);
        const shipments = data?.shipments || data || [];
        const total = shipments.length;
        const inTransit = shipments.filter(s => (s.status || '').toLowerCase() === 'in transit').length;
        const delivered = shipments.filter(s => (s.status || '').toLowerCase() === 'delivered').length;
        const pending = shipments.filter(s => (s.status || '').toLowerCase() === 'pending').length;
        setStats({ totalShipments: total, inTransit, delivered, pending });
        const mapped = shipments
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 5)
          .map(s => ({
            id: s.trackingCode || s._id,
            origin: s.senderState || s.senderCity || s.senderAddress || '—',
            destination: s.receiverState || s.receiverCity || s.receiverAddress || '—',
            status: s.status || 'Pending',
            date: s.createdAt ? new Date(s.createdAt).toLocaleDateString() : '—'
          }));
        setRecentShipments(mapped);
      } catch (err) {
        console.warn('Failed to load dashboard shipments:', err?.message || err);
      }
    })();
  }, []);

  return (
    <div className="space-y-6 pb-24">
      <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl p-6 text-white shadow-lg">
        <h1 className="text-2xl font-bold mb-2">Welcome back, {userLocal?.name || 'User'}! 👋</h1>
        <p className="text-red-50">Here's what's happening with your shipments today</p>
      </div>

      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-gray-800 px-1">Quick Actions</h2>
        <Link
          to="/dashboard/user/shipments/new"
          className="block bg-gradient-to-r from-red-500 to-red-600 rounded-2xl p-6 text-white shadow-lg transform transition-all duration-300 hover:scale-105 active:scale-95"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mr-4">
                  <FontAwesomeIcon icon={faPlus} className="text-2xl" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Book New Shipment</h3>
                  <p className="text-red-50 text-sm">Send a package anywhere</p>
                </div>
              </div>
            </div>
            <FontAwesomeIcon icon={faChevronRight} className="text-2xl opacity-70" />
          </div>
        </Link>

        <div className="grid grid-cols-2 gap-3">
          <Link
            to="/dashboard/user/shipments/tracking"
            className="bg-white rounded-xl p-5 shadow-md border-2 border-gray-100 transform transition-all duration-300 hover:scale-105 active:scale-95"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-3 mx-auto">
              <FontAwesomeIcon icon={faSearchLocation} className="text-blue-600 text-xl" />
            </div>
            <h3 className="font-semibold text-gray-800 text-sm">Track Package</h3>
            <p className="text-xs text-gray-500 mt-1">Live tracking</p>
          </Link>
          <Link
            to="/dashboard/user/shipments"
            className="bg-white rounded-xl p-5 shadow-md border-2 border-gray-100 transform transition-all duration-300 hover:scale-105 active:scale-95"
          >
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-3 mx-auto">
              <FontAwesomeIcon icon={faBox} className="text-purple-600 text-xl" />
            </div>
            <h3 className="font-semibold text-gray-800 text-sm">My Shipments</h3>
            <p className="text-xs text-gray-500 mt-1">View all</p>
          </Link>
        </div>
      </div>

      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-gray-800 px-1">Overview</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl p-5 shadow-md border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <FontAwesomeIcon icon={faBox} className="text-blue-600" />
              </div>
              <span className="text-2xl font-bold text-gray-800">{stats.totalShipments}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Total</h3>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-md border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <FontAwesomeIcon icon={faTruck} className="text-orange-600" />
              </div>
              <span className="text-2xl font-bold text-gray-800">{stats.inTransit}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">In Transit</h3>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-md border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
              </div>
              <span className="text-2xl font-bold text-gray-800">{stats.delivered}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Delivered</h3>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-md border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600" />
              </div>
              <span className="text-2xl font-bold text-gray-800">{stats.pending}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Pending</h3>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
        <div className="flex justify-between items-center p-5 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800">Recent Shipments</h2>
          <Link to="/dashboard/user/shipments" className="text-red-600 text-sm font-medium">
            View All
          </Link>
        </div>
        <div className="divide-y divide-gray-100">
          {recentShipments.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FontAwesomeIcon icon={faBox} className="text-4xl mb-3 text-gray-300" />
              <p>No shipments yet</p>
              <Link to="/dashboard/user/shipments/new" className="text-red-600 text-sm font-medium mt-2 inline-block">
                Create your first shipment
              </Link>
            </div>
          ) : (
            recentShipments.map((shipment) => (
              <div key={shipment.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center mb-1">
                      <span className="font-semibold text-gray-800 text-sm">{shipment.id}</span>
                      <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${shipment.status === 'Delivered' ? 'bg-green-100 text-green-700' :
                        shipment.status === 'In Transit' ? 'bg-blue-100 text-blue-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                        {shipment.status}
                      </span>
                    </div>
                    <div className="flex items-center text-xs text-gray-500 space-x-2">
                      <span className="flex items-center">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-1" />
                        {shipment.origin}
                      </span>
                      <span>→</span>
                      <span>{shipment.destination}</span>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400">{shipment.date}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

    </div>
  );
};


const UserDashboard = () => {
  useWelcomeOnce('user_dashboard', 'Welcome! 📦 Book or track shipments from the menu.');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const statusMapRef = useRef({});
  const [notifications, setNotifications] = useState([
    { id: 1, type: 'info', title: 'New Feature Available', message: 'You can now track shipments in real-time', time: '2 hours ago' },
    { id: 2, type: 'success', title: 'Shipment Delivered', message: 'Your shipment SH12345 has been delivered', time: '1 day ago' },
    { id: 3, type: 'warning', title: 'Payment Pending', message: 'Payment for shipment SH67890 is pending', time: '2 days ago' }
  ]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [profileUpdateNotice, setProfileUpdateNotice] = useState(false);

  // Check user session on component mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const me = await userService.getMe();
        if (me) {
          setUser(me);
        } else {
          const localUser = localStorage.getItem('user');
          setUser(localUser ? JSON.parse(localUser) : null);
        }
      } catch (e) {
        const localUser = localStorage.getItem('user');
        setUser(localUser ? JSON.parse(localUser) : null);
      } finally {
        setSessionChecked(true);
      }
    };
    checkSession();
  }, []);

  useEffect(() => {
    // Initialize push notifications after session check
    (async () => {
      if (sessionChecked && user && user.email) {
        try {
          await pushService.initForEmail(user.email);
        } catch (err) {
          console.warn('Push subscription failed:', err);
        }
      }
    })();
  }, [sessionChecked, user]);

  // Subscribe to shipment stream for live in-site notifications
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const es = shipmentService.subscribeStream(token);
    es.onmessage = (evt) => {
      try {
        const payload = JSON.parse(evt.data || '{}');
        const shipments = payload?.shipments || payload || [];
        shipments.forEach((s) => {
          const id = s.trackingCode || s._id || s.id;
          const curr = (s.status || '').trim();
          const prev = statusMapRef.current[id];
          if (!prev) {
            statusMapRef.current[id] = curr;
            return;
          }
          if (prev !== curr) {
            statusMapRef.current[id] = curr;
            const type = curr.toLowerCase() === 'delivered' ? 'success' : (curr.toLowerCase() === 'in transit' ? 'info' : 'warning');
            const message = `Shipment ${id} status changed to ${curr}`;
            setNotifications((n) => [
              { id: Date.now() + Math.random(), type, title: 'Shipment Updated', message, time: 'just now' },
              ...n
            ]);
          }
        });
      } catch (e) {
        console.warn('Failed to parse stream message', e);
      }
    };
    es.onerror = () => {
      try { es.close(); } catch { }
    };
    return () => {
      try { es.close(); } catch { }
    };
  }, []);

  // Show profile update success banner when redirected from profile
  useEffect(() => {
    if (location?.state?.profileUpdated) {
      setProfileUpdateNotice(true);
      setTimeout(() => setProfileUpdateNotice(false), 3000);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location?.state]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
  };

  const handleSignOut = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('role');
    navigate('/auth');
  };

  const handleTrackingClick = () => {
    navigate('/dashboard/user/shipments/tracking');
    setSidebarOpen(false);
  };

  // Determine active tab based on current path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/shipments') && !path.includes('/new') && !path.includes('/tracking')) {
      return 'shipments';
    }
    if (path.includes('/shipments/new')) return 'new-shipment';
    if (path.includes('/shipments/tracking')) return 'tracking';
    if (path.includes('/profile')) return 'profile';
    if (path.includes('/bundles')) return 'bundles';
    return 'dashboard';
  };

  const activeTab = getActiveTab();
  const isHomeActive = activeTab === 'dashboard';
  const isHistoryActive = activeTab === 'shipments' || activeTab === 'tracking';
  const isBookActive = activeTab === 'new-shipment';
  const isProfileActive = activeTab === 'profile';
  const isBundlesActive = activeTab === 'bundles';

  const getTabTitle = () => {
    switch (activeTab) {
      case 'dashboard': return 'Dashboard';
      case 'shipments': return 'My Shipments';
      case 'new-shipment': return 'New Shipment';
      case 'tracking': return 'Track Shipment';
      case 'profile': return 'My Profile';
      case 'bundles': return 'Bulk Shipments';
      default: return 'Dashboard';
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success': return faCheckCircle;
      case 'warning': return faExclamationTriangle;
      case 'info': return faInfoCircle;
      default: return faInfoCircle;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'success': return 'text-green-500';
      case 'warning': return 'text-yellow-500';
      case 'info': return 'text-blue-500';
      default: return 'text-blue-500';
    }
  };

  // Enhanced Dashboard Home Component
  const DashboardHome = () => {
    const [stats, setStats] = useState({ totalShipments: 0, inTransit: 0, delivered: 0, pending: 0 });
    const [recentShipments, setRecentShipments] = useState([]);

    useEffect(() => {
      const token = localStorage.getItem('token');
      if (!token) return;
      (async () => {
        try {
          const data = await shipmentService.getMy(token);
          const shipments = data?.shipments || data || [];
          const total = shipments.length;
          const inTransit = shipments.filter(s => (s.status || '').toLowerCase() === 'in transit').length;
          const delivered = shipments.filter(s => (s.status || '').toLowerCase() === 'delivered').length;
          const pending = shipments.filter(s => (s.status || '').toLowerCase() === 'pending').length;
          setStats({ totalShipments: total, inTransit, delivered, pending });
          const mapped = shipments
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 5)
            .map(s => ({
              id: s.trackingCode || s._id,
              origin: s.senderState || s.senderCity || s.senderAddress || '—',
              destination: s.receiverState || s.receiverCity || s.receiverAddress || '—',
              status: s.status || 'Pending',
              date: s.createdAt ? new Date(s.createdAt).toLocaleDateString() : '—'
            }));
          setRecentShipments(mapped);
        } catch (err) {
          console.warn('Failed to load dashboard shipments:', err?.message || err);
        }
      })();
    }, []);

    return (
      <div className="space-y-6">
        {/* Welcome Section with gradient */}
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl p-6 text-white shadow-lg">
          <h1 className="text-2xl font-bold mb-2">Welcome back, {user.name}! 👋</h1>
          <p className="text-red-50">Here's what's happening with your shipments today</p>
        </div>

        {/* Quick Action Cards - PROMINENT */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-800 px-1">Quick Actions</h2>

          {/* Book New Shipment - HERO CTA */}
          <Link
            to="/dashboard/user/shipments/new"
            className="block bg-gradient-to-r from-red-500 to-red-600 rounded-2xl p-6 text-white shadow-lg transform transition-all duration-300 hover:scale-105 active:scale-95"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mr-4">
                    <FontAwesomeIcon icon={faPlus} className="text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Book New Shipment</h3>
                    <p className="text-red-50 text-sm">Send a package anywhere</p>
                  </div>
                </div>
              </div>
              <FontAwesomeIcon icon={faChevronRight} className="text-2xl opacity-70" />
            </div>
          </Link>

          {/* Other Quick Actions */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleTrackingClick}
              className="bg-white rounded-xl p-5 shadow-md border-2 border-gray-100 transform transition-all duration-300 hover:scale-105 active:scale-95"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-3 mx-auto">
                <FontAwesomeIcon icon={faSearchLocation} className="text-blue-600 text-xl" />
              </div>
              <h3 className="font-semibold text-gray-800 text-sm">Track Package</h3>
              <p className="text-xs text-gray-500 mt-1">Live tracking</p>
            </button>

            <Link
              to="/dashboard/user/shipments"
              className="bg-white rounded-xl p-5 shadow-md border-2 border-gray-100 transform transition-all duration-300 hover:scale-105 active:scale-95"
            >
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-3 mx-auto">
                <FontAwesomeIcon icon={faBox} className="text-purple-600 text-xl" />
              </div>
              <h3 className="font-semibold text-gray-800 text-sm">My Shipments</h3>
              <p className="text-xs text-gray-500 mt-1">View all</p>
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-800 px-1">Overview</h2>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-xl p-5 shadow-md border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FontAwesomeIcon icon={faBox} className="text-blue-600" />
                </div>
                <span className="text-2xl font-bold text-gray-800">{stats.totalShipments}</span>
              </div>
              <h3 className="text-sm font-medium text-gray-600">Total</h3>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-md border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <FontAwesomeIcon icon={faTruck} className="text-orange-600" />
                </div>
                <span className="text-2xl font-bold text-gray-800">{stats.inTransit}</span>
              </div>
              <h3 className="text-sm font-medium text-gray-600">In Transit</h3>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-md border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                </div>
                <span className="text-2xl font-bold text-gray-800">{stats.delivered}</span>
              </div>
              <h3 className="text-sm font-medium text-gray-600">Delivered</h3>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-md border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600" />
                </div>
                <span className="text-2xl font-bold text-gray-800">{stats.pending}</span>
              </div>
              <h3 className="text-sm font-medium text-gray-600">Pending</h3>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
          <div className="flex justify-between items-center p-5 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-800">Recent Shipments</h2>
            <Link to="/dashboard/user/shipments" className="text-red-600 text-sm font-medium">
              View All
            </Link>
          </div>

          <div className="divide-y divide-gray-100">
            {recentShipments.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <FontAwesomeIcon icon={faBox} className="text-4xl mb-3 text-gray-300" />
                <p>No shipments yet</p>
                <Link to="/dashboard/user/shipments/new" className="text-red-600 text-sm font-medium mt-2 inline-block">
                  Create your first shipment
                </Link>
              </div>
            ) : (
              recentShipments.map((shipment) => (
                <div key={shipment.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center mb-1">
                        <span className="font-semibold text-gray-800 text-sm">{shipment.id}</span>
                        <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${shipment.status === 'Delivered' ? 'bg-green-100 text-green-700' :
                          shipment.status === 'In Transit' ? 'bg-blue-100 text-blue-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                          {shipment.status}
                        </span>
                      </div>
                      <div className="flex items-center text-xs text-gray-500 space-x-2">
                        <span className="flex items-center">
                          <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-1" />
                          {shipment.origin}
                        </span>
                        <span>→</span>
                        <span>{shipment.destination}</span>
                      </div>
                    </div>
                    <span className="text-xs text-gray-400">{shipment.date}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  // Show loading while checking session
  if (!sessionChecked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-700 font-medium">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // If user is not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-white flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FontAwesomeIcon icon={faUser} className="text-red-600 text-2xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Session Expired</h2>
            <p className="text-gray-600 mt-2">Please log in again to continue</p>
          </div>
          <Link
            to="/auth"
            className="w-full py-3 px-4 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 inline-block text-center transition-colors"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Enhanced Mobile Navbar */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white shadow-sm z-50 border-b border-gray-100">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={toggleSidebar}
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 text-gray-700 focus:outline-none hover:bg-gray-200 transition-colors"
          >
            <FontAwesomeIcon icon={sidebarOpen ? faTimes : faBars} className="text-lg" />
          </button>
          <h1 className="text-lg font-bold text-gray-800">{getTabTitle()}</h1>
          <div className="relative">
            <button
              onClick={toggleNotifications}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 text-gray-700 focus:outline-none hover:bg-gray-200 transition-colors relative"
            >
              <FontAwesomeIcon icon={faBell} className="text-lg" />
              {notifications.length > 0 && (
                <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white"></span>
              )}
            </button>

            {/* Enhanced Notification Dropdown */}
            {showNotifications && (
              <>
                <div className="fixed inset-0 z-40" onClick={toggleNotifications}></div>
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-2xl z-50 border border-gray-100 max-h-96 overflow-hidden">
                  <div className="p-4 border-b border-gray-100 bg-gray-50">
                    <h3 className="text-lg font-bold text-gray-800">Notifications</h3>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <FontAwesomeIcon icon={faBell} className="text-4xl mb-2 text-gray-300" />
                        <p>No new notifications</p>
                      </div>
                    ) : (
                      notifications.map(notification => (
                        <div
                          key={notification.id}
                          className="p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start">
                            <div className={`mr-3 mt-1 ${getNotificationColor(notification.type)}`}>
                              <FontAwesomeIcon icon={getNotificationIcon(notification.type)} />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-sm text-gray-800">{notification.title}</h4>
                              <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                              <p className="text-xs text-gray-400 mt-2">{notification.time}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Mobile Sidebar */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={toggleSidebar}></div>
          <div className="fixed left-0 top-0 bottom-0 w-80 bg-white shadow-2xl z-50 transform transition-transform">
            <div className="p-6 bg-gradient-to-br from-red-500 to-red-600">
              <div className="flex items-center">
                <div className="w-14 h-14 rounded-2xl bg-white bg-opacity-20 backdrop-blur-sm flex items-center justify-center mr-4">
                  <FontAwesomeIcon icon={faUser} className="text-white text-xl" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg">{user.name}</h3>
                  <p className="text-sm text-red-50 capitalize">{user.type}: {user.name}</p>
                </div>
              </div>
            </div>
            <nav className="p-4">
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/dashboard/user"
                    onClick={() => setSidebarOpen(false)}
                    className={`w-full text-left px-4 py-3.5 rounded-xl flex items-center transition-all ${activeTab === 'dashboard'
                      ? 'bg-red-50 text-red-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    <FontAwesomeIcon icon={faHome} className="mr-3 w-5" />
                    Dashboard
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dashboard/user/shipments/new"
                    onClick={() => setSidebarOpen(false)}
                    className={`w-full text-left px-4 py-3.5 rounded-xl flex items-center transition-all ${activeTab === 'new-shipment'
                      ? 'bg-red-50 text-red-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-3 w-5" />
                    Book Shipment
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dashboard/user/bundles"
                    onClick={() => setSidebarOpen(false)}
                    className={`w-full text-left px-4 py-3.5 rounded-xl flex items-center transition-all ${activeTab === 'bundles'
                      ? 'bg-red-50 text-red-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    <FontAwesomeIcon icon={faBoxOpen} className="mr-3 w-5" />
                    Bulk Shipments
                  </Link>
                </li>
                <li>
                  <button
                    onClick={handleTrackingClick}
                    className={`w-full text-left px-4 py-3.5 rounded-xl flex items-center transition-all ${activeTab === 'tracking'
                      ? 'bg-red-50 text-red-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    <FontAwesomeIcon icon={faSearchLocation} className="mr-3 w-5" />
                    Track Package
                  </button>
                </li>
                <li>
                  <Link
                    to="/dashboard/user/shipments"
                    onClick={() => setSidebarOpen(false)}
                    className={`w-full text-left px-4 py-3.5 rounded-xl flex items-center transition-all ${activeTab === 'shipments'
                      ? 'bg-red-50 text-red-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    <FontAwesomeIcon icon={faBox} className="mr-3 w-5" />
                    My Shipments
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dashboard/user/profile"
                    onClick={() => setSidebarOpen(false)}
                    className={`w-full text-left px-4 py-3.5 rounded-xl flex items-center transition-all ${activeTab === 'profile'
                      ? 'bg-red-50 text-red-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    <FontAwesomeIcon icon={faUser} className="mr-3 w-5" />
                    My Profile
                  </Link>
                </li>
                <li className="pt-4 mt-4 border-t border-gray-200">
                  <Link
                    to="/auth"
                    onClick={handleSignOut}
                    className="w-full text-left px-4 py-3.5 rounded-xl flex items-center text-red-600 hover:bg-red-50 transition-all"
                  >
                    <FontAwesomeIcon icon={faSignOutAlt} className="mr-3 w-5" />
                    Sign Out
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8 pt-20 md:pt-6 pb-24">
        <AnnouncementBanner targetAudience="user" />
        {profileUpdateNotice && (
          <div className="mb-4 animate-fade-in">
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center shadow-sm">
              <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
              <span className="font-medium">Profile updated successfully</span>
            </div>
          </div>
        )}
        <div className="flex flex-col md:flex-row">
          {/* Desktop Sidebar */}
          <div className="hidden md:block md:w-64 mb-6 md:mb-0 md:mr-6">
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 sticky top-6">
              <div className="mb-6 p-4 border-b border-gray-100">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center mr-3">
                    <FontAwesomeIcon icon={faUser} className="text-red-600 text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">{user.name}</h3>
                    <p className="text-sm text-gray-500 capitalize">{user.type}: {user.name}</p>
                  </div>
                </div>
              </div>
              <nav>
                <ul className="space-y-2">
                  <li>
                    <Link
                      to="/dashboard/user"
                      className={`w-full text-left px-4 py-3 rounded-xl flex items-center transition-all ${activeTab === 'dashboard' ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      <FontAwesomeIcon icon={faHome} className="mr-3" />
                      Dashboard
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/dashboard/user/shipments/new"
                      className={`w-full text-left px-4 py-3 rounded-xl flex items-center transition-all ${activeTab === 'new-shipment' ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-3" />
                      New Shipment
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/dashboard/user/bundles"
                      className={`w-full text-left px-4 py-3 rounded-xl flex items-center transition-all ${activeTab === 'bundles' ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      <FontAwesomeIcon icon={faBoxOpen} className="mr-3" />
                      Bulk Shipments
                    </Link>
                  </li>
                  <li>
                    <button
                      onClick={handleTrackingClick}
                      className={`w-full text-left px-4 py-3 rounded-xl flex items-center transition-all ${activeTab === 'tracking' ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      <FontAwesomeIcon icon={faSearchLocation} className="mr-3" />
                      Track Shipment
                    </button>
                  </li>
                  <li>
                    <Link
                      to="/dashboard/user/shipments"
                      className={`w-full text-left px-4 py-3 rounded-xl flex items-center transition-all ${activeTab === 'shipments' ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      <FontAwesomeIcon icon={faBox} className="mr-3" />
                      My Shipments
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/dashboard/user/profile"
                      className={`w-full text-left px-4 py-3 rounded-xl flex items-center transition-all ${activeTab === 'profile' ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      <FontAwesomeIcon icon={faUser} className="mr-3" />
                      My Profile
                    </Link>
                  </li>
                  <li className="pt-2 mt-2 border-t border-gray-100">
                    <Link
                      to="/auth"
                      onClick={handleSignOut}
                      className="w-full text-left px-4 py-3 rounded-xl flex items-center text-red-600 hover:bg-red-50 transition-all"
                    >
                      <FontAwesomeIcon icon={faSignOutAlt} className="mr-3" />
                      Sign Out
                    </Link>
                  </li>
                </ul>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 pb-20 md:pb-6">
            <Outlet />
          </div>
        </div>
      </div>
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50">
        <div className="grid grid-cols-5 gap-1 px-2 py-2">
          <button onClick={() => navigate('/dashboard/user')} className={`flex flex-col items-center justify-center py-2 rounded-lg transition-all ${isHomeActive ? 'text-red-600' : 'text-gray-500'}`}>
            <FontAwesomeIcon icon={faHome} className="text-xl mb-1" />
            <span className="text-xs font-medium">Home</span>
          </button>
          <button onClick={() => navigate('/dashboard/user/shipments')} className={`flex flex-col items-center justify-center py-2 rounded-lg transition-all ${isHistoryActive ? 'text-red-600' : 'text-gray-500'}`}>
            <FontAwesomeIcon icon={faHistory} className="text-xl mb-1" />
            <span className="text-xs font-medium">History</span>
          </button>
          <button onClick={() => navigate('/dashboard/user/shipments/new')} className="flex flex-col items-center justify-center -mt-6">
            <div className="relative z-50 w-16 h-16 rounded-full bg-gradient-to-br from-red-500 to-red-600 shadow-lg flex items-center justify-center transform transition-transform active:scale-95">
              <FontAwesomeIcon icon={faPlus} className="text-white text-2xl" />
            </div>
            <span className="text-xs font-medium text-gray-700 mt-1">Book</span>
          </button>
          <button onClick={() => navigate('/dashboard/user/profile')} className={`flex flex-col items-center justify-center py-2 rounded-lg transition-all ${isProfileActive ? 'text-red-600' : 'text-gray-500'}`}>
            <FontAwesomeIcon icon={faUser} className="text-xl mb-1" />
            <span className="text-xs font-medium">Profile</span>
          </button>
          <button onClick={() => navigate('/auth/plan-selection')} className="flex flex-col items-center justify-center py-2 rounded-lg transition-all text-gray-500">
            <FontAwesomeIcon icon={faCrown} className="text-xl mb-1" />
            <span className="text-xs font-medium">Plans</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
